import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CreditCard,
  Pencil,
  Trash2,
  ArrowLeft,
  Eye,
  Banknote,
  Info,
  ClipboardList,
} from 'lucide-react';
import { paymentService } from '../../services/paymentService.js';
import { shipmentService } from '../../services/shipmentService.js';
import Alert from '../common/Alert.js';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

export default function ViewPayment() {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [allPayments, setAllPayments] = useState([]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [paymentId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);

      const paymentResponse = await paymentService.getPaymentById(paymentId);
      const paymentData = paymentResponse.data.data;
      setPayment(paymentData);

      const shipmentResponse = await shipmentService.getShipment(paymentData.shipment_id);
      const shipment = shipmentResponse.data.shipment || shipmentResponse.data.data;
      setShipmentDetails(shipment);

      const allPaymentsResponse = await paymentService.getPaymentHistory(paymentData.shipment_id);
      setAllPayments(allPaymentsResponse.data.data.payment_history || []);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch payment details');
      navigate('/payment-history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await Alert.confirm(
      `Delete payment record of ₹${payment.payment_amount?.toFixed(2)} for shipment ${payment.shipment_id}?`,
      'Delete Payment Record'
    );

    if (!confirmed) return;

    try {
      await paymentService.deletePayment(payment.id);
      Alert.success('Payment record deleted successfully');
      navigate('/payment-history');
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to delete payment');
    }
  };

  const formatPaymentDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'N/A';

  const formatShortDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'N/A';

  if (loading) {
    return (
      <PageLayout title="Payment Details" icon={CreditCard}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading payment details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!payment || !shipmentDetails) {
    return (
      <PageLayout title="Payment Details" icon={CreditCard}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-gray-600 text-lg font-semibold">Payment not found</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Payment Details"
      icon={CreditCard}
      maxWidth="max-w-5xl"
      actions={
        <>
          <Button
            variant="secondary"
            icon={Pencil}
            onClick={() => navigate(`/payment-history/edit/${payment.id}`)}
          >
            Edit
          </Button>
          <Button variant="danger" icon={Trash2} onClick={handleDelete}>
            Delete
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/payment-history')}>
            Back to Payment History
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="bg-primary rounded-card p-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-secondary">Payment ID: #{payment.id}</h2>
        </div>

        <DetailCard title="Payment Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 rounded-card p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Banknote className="w-5 h-5 text-green-600" />
                <p className="text-sm font-bold text-green-700 uppercase tracking-wide">Payment Amount</p>
              </div>
              <p className="text-3xl font-bold text-green-700">₹{payment.payment_amount?.toFixed(2)}</p>
            </div>

            <div className="bg-brand-light rounded-card p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-brand" />
                <p className="text-sm font-bold text-brand uppercase tracking-wide">Payment Mode</p>
              </div>
              <span className="inline-block px-4 py-2 bg-brand text-white rounded-lg text-lg font-bold uppercase">
                {payment.payment_mode?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>

          <DetailRow label="Previous Balance" value={`₹${payment.previous_balance?.toFixed(2)}`} />
          <DetailRow
            label="New Balance"
            value={
              <span className="font-bold text-red-700">₹{payment.new_balance?.toFixed(2)}</span>
            }
          />
          <DetailRow label="Payment Date" value={formatPaymentDate(payment.payment_date)} />
          <DetailRow
            label="Recorded By"
            value={
              <span>
                {payment.recorded_by_name}
                <span className="block text-sm text-gray-600 mt-1">ID: {payment.recorded_by_user_id}</span>
              </span>
            }
          />
          {payment.remarks && (
            <div className="mt-4 bg-amber-50 rounded-card p-4 border border-amber-200">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-amber-800 uppercase tracking-wide mb-1">Remarks</p>
                  <p className="text-secondary">{payment.remarks}</p>
                </div>
              </div>
            </div>
          )}
        </DetailCard>

        <DetailCard title="Associated Shipment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 rounded-card p-4 border border-blue-200">
              <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Shipment ID</p>
              <button
                type="button"
                onClick={() => navigate(`/shipments/view/${shipmentDetails.shipment_id}`)}
                className="font-mono font-bold text-brand hover:underline text-lg"
              >
                #{shipmentDetails.shipment_id}
              </button>
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Status</p>
              <StatusBadge status={shipmentDetails.status} />
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Sender</p>
              <p className="font-bold text-secondary">{shipmentDetails.sender_name}</p>
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Receiver</p>
              <p className="font-bold text-secondary">{shipmentDetails.receiver_name}</p>
            </div>

            <div className="bg-brand-light rounded-card p-4 border border-blue-200">
              <p className="text-xs font-medium text-brand uppercase tracking-wide mb-1">Total Amount</p>
              <p className="font-bold text-brand text-lg">₹{shipmentDetails.total_amount?.toFixed(2)}</p>
            </div>

            <div className="bg-green-50 rounded-card p-4 border border-green-200">
              <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-1">Paid Amount</p>
              <p className="font-bold text-green-700 text-lg">₹{shipmentDetails.paid_amount?.toFixed(2)}</p>
            </div>

            <div
              className={`rounded-card p-4 border ${
                shipmentDetails.balance_amount === 0
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <p
                className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                  shipmentDetails.balance_amount === 0 ? 'text-green-700' : 'text-red-700'
                }`}
              >
                Balance Amount
              </p>
              <p
                className={`font-bold text-lg ${
                  shipmentDetails.balance_amount === 0 ? 'text-green-700' : 'text-red-700'
                }`}
              >
                ₹{shipmentDetails.balance_amount?.toFixed(2)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-card p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Agent</p>
              <p className="font-bold text-secondary">{shipmentDetails.agent_name || 'N/A'}</p>
            </div>
          </div>

          <Button
            icon={Eye}
            onClick={() => navigate(`/shipments/view/${shipmentDetails.shipment_id}`)}
            className="w-full"
          >
            View Full Shipment Details
          </Button>
        </DetailCard>

        <DetailCard title="Payment History for This Shipment">
          {allPayments.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No payment records found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allPayments.map((p) => (
                <div
                  key={p.id}
                  className={`rounded-card p-4 border transition-all ${
                    p.id === payment.id
                      ? 'bg-purple-50 border-purple-400 shadow-lg'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {p.id === payment.id && (
                          <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded uppercase">
                            Current
                          </span>
                        )}
                        <span className="text-sm text-gray-600">{formatShortDate(p.payment_date)}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Amount: </span>
                          <span className="font-bold text-green-600">₹{p.payment_amount?.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Mode: </span>
                          <span className="font-semibold">{p.payment_mode?.replace(/_/g, ' ')}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">By: </span>
                          <span className="font-semibold">{p.recorded_by_name}</span>
                        </div>
                      </div>
                      {p.remarks && (
                        <p className="text-xs text-gray-600 mt-2 italic">&quot;{p.remarks}&quot;</p>
                      )}
                    </div>
                    {p.id !== payment.id && (
                      <Button
                        size="sm"
                        icon={Eye}
                        onClick={() => navigate(`/payment-history/view/${p.id}`)}
                      >
                        View
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DetailCard>
      </div>
    </PageLayout>
  );
}
